<template>
  <nav
    class="flex items-center gap-2 bg-neutral-background-subtle border-neutral-border-default rounded-lg px-1.5 py-1"
    aria-label="Pagination"
  >
    <PageButton
      :icon="ChevronDoubleLeftIcon"
      :disabled="page === 1"
      title="First page"
      sr-text="First"
      @click="goToPage(1)"
    />
    <PageButton
      :icon="ChevronLeftIcon"
      :disabled="page === 1"
      sr-text="Previous"
      @click="prevPage"
    />

    <template v-for="item in paginationItems" :key="item">
      <PageButton
        :content="item"
        :is-active="page === item"
        @click="goToPage(item as number)"
      />
    </template>

    <PageButton
      :icon="ChevronRightIcon"
      :disabled="page === totalPages"
      sr-text="Next"
      @click="nextPage"
    />
    <PageButton
      :icon="ChevronDoubleRightIcon"
      :disabled="page === totalPages"
      title="Last page"
      sr-text="Last"
      @click="goToPage(totalPages)"
    />
  </nav>
</template>

<script setup lang="ts">
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/vue/24/outline'
import { computed } from 'vue'
import PageButton from './PaginationButton.vue'

const VISIBLE_PAGE_BUTTONS = 3

const page = defineModel<number>('page', { default: 1 })
const itemsPerPage = defineModel<number>('itemsPerPage', { default: 5 })

const props = defineProps<{
  totalItems: number
  itemsLabel?: string
}>()

const totalPages = computed(() =>
  Math.ceil(props.totalItems / itemsPerPage.value),
)

const paginationItems = computed(() => {
  const items = []

  if (totalPages.value <= VISIBLE_PAGE_BUTTONS) {
    for (let i = 1; i <= totalPages.value; i++) {
      items.push(i)
    }
  } else {
    const halfVisible = Math.floor(VISIBLE_PAGE_BUTTONS / 2)
    let start = Math.max(1, page.value - halfVisible)
    let end = Math.min(totalPages.value, page.value + halfVisible)

    if (end - start + 1 < VISIBLE_PAGE_BUTTONS) {
      if (start === 1) {
        end = Math.min(totalPages.value, start + VISIBLE_PAGE_BUTTONS - 1)
      } else {
        start = Math.max(1, end - VISIBLE_PAGE_BUTTONS + 1)
      }
    }

    for (let i = start; i <= end; i++) {
      items.push(i)
    }
  }

  return items
})

function prevPage() {
  if (page.value > 1) {
    page.value--
  }
}

function nextPage() {
  if (page.value < totalPages.value) {
    page.value++
  }
}

function goToPage(newPage: number) {
  page.value = newPage
}
</script>
